"use client";

import { useEffect, useEffectEvent, useState } from "react";

import { useHash } from "@/lib/hooks/use-hash";
import { AppModalKey, type AppModalProps, useAppModal } from "~/modals";

type ImageGalleryModalProps = AppModalProps[AppModalKey.ImageGalleryModal];
type PhotoViewerModalProps = AppModalProps[AppModalKey.PhotoViewerModal];

type UseGalleryHashModalsArgs = {
  /** The gallery's props, less the handlers this hook supplies. */
  gallery: Omit<ImageGalleryModalProps, "handleImageClick" | "onClose">;
  /** The media id the viewer opens on for the gallery item at `index`. */
  getImageId: (index: number) => string | undefined;
  /** The viewer's props, less the position and close handler this hook supplies. */
  photoViewer: Omit<PhotoViewerModalProps, "onClose" | "startingIndex">;
};

/**
 * Keeps the gallery modals in step with the URL hash: `#type=` opens the image gallery,
 * and `#imageID=` opens the photo viewer on top of it. The hash stays the trigger, so
 * deep links and the back button keep working; going back from a modal clears its part
 * of the hash. The page closes both modals when it unmounts.
 */
const useGalleryHashModals = ({
  gallery,
  getImageId,
  photoViewer,
}: UseGalleryHashModalsArgs) => {
  const { hash, hashMap, updateHash } = useHash();
  const imageGalleryModal = useAppModal(AppModalKey.ImageGalleryModal);
  const photoViewerModal = useAppModal(AppModalKey.PhotoViewerModal);
  const [startingIndex, setStartingIndex] = useState(0);

  // `updateHash` merges into the current hash, so the gallery's `type` stays put.
  const handleImageClick = (index: number) => {
    const imageID = getImageId(index);
    if (imageID) {
      updateHash({ imageID });
    }
    setStartingIndex(index);
  };

  const closeGallery = () => {
    updateHash({ type: null, imageID: null });
  };

  const closePhotoViewer = () => {
    updateHash({ imageID: null });
  };

  // An effect event, so it opens the modals with the latest props without the effect
  // re-running on them; the props and the modal hooks are new objects every render.
  const syncModals = useEffectEvent(() => {
    if (hashMap.get("type")) {
      imageGalleryModal.openModal({
        ...gallery,
        handleImageClick,
        onClose: closeGallery,
      });
    } else {
      imageGalleryModal.closeModal();
    }

    if (hashMap.get("imageID")) {
      photoViewerModal.openModal({
        ...photoViewer,
        onClose: closePhotoViewer,
        startingIndex,
      });
    } else {
      photoViewerModal.closeModal();
    }
  });

  // The hash, plus every prop that can change while the modals are open: re-opening
  // replaces the props, so the modals never show stale ones.
  useEffect(() => {
    syncModals();
  }, [
    hash,
    gallery.activeMediaType,
    gallery.isFav,
    gallery.isMDScreen,
    startingIndex,
  ]);

  return { setStartingIndex };
};

export { useGalleryHashModals };
